import dayjs from "dayjs";

import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";

function getWelshGreeting() {
    const currentHour = new Date().getHours();

    if (currentHour < 12) {
        return "Bore Da";
    }

    if (currentHour < 18) {
        return "Prynhawn Da";
    }

    return "Noswaith dda";
}

export default function NoNotificationsCard({
    compact = false,
    firstName = "",
}) {
    const greeting = firstName
        ? `${getWelshGreeting()}, ${firstName}`
        : getWelshGreeting();
    const today = dayjs();

    return (
        <Card
            variant="outlined"
            sx={{
                position: "relative",
                overflow: "hidden",
                mb: 2,
                minHeight: compact ? 104 : 360,
                borderRadius: 3,
                borderColor: "divider",
                backgroundColor: "background.paper",
                boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                        ? "0 5px 22px rgba(0, 0, 0, 0.28)"
                        : "0 5px 22px rgba(20, 35, 25, 0.065)",
                transition:
                    "min-height 650ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 300ms ease",
                willChange: "min-height",
                "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                    willChange: "auto",
                },
            }}
        >
            <Box
                sx={{
                    minHeight: compact ? 104 : 360,
                    display: "grid",
                    gridTemplateColumns: compact
                        ? {
                              xs: "82px minmax(0, 1fr)",
                              sm: "210px minmax(0, 1fr)",
                          }
                        : {
                              xs: "1fr",
                              sm: "minmax(240px, 0.82fr) minmax(0, 1.18fr)",
                          },
                    transition:
                        "min-height 650ms cubic-bezier(0.22, 1, 0.36, 1), grid-template-columns 600ms cubic-bezier(0.22, 1, 0.36, 1)",
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        minHeight: compact ? 104 : { xs: 172, sm: 360 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: compact ? "center" : "space-between",
                        px: compact
                            ? { xs: 1.5, sm: 2.25 }
                            : { xs: 2.5, sm: 3.5 },
                        py: compact ? 1.5 : { xs: 2.5, sm: 3.5 },
                        color: "common.white",
                        backgroundColor: "rgb(0, 149, 61)",
                        backgroundImage:
                            "linear-gradient(145deg, rgb(0, 126, 51) 0%, rgb(0, 149, 61) 58%, rgb(0, 119, 49) 100%)",
                        transition:
                            "min-height 650ms cubic-bezier(0.22, 1, 0.36, 1), padding 600ms cubic-bezier(0.22, 1, 0.36, 1)",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: compact ? 160 : 245,
                            height: compact ? 160 : 245,
                            top: compact ? -105 : -112,
                            right: compact ? -105 : -122,
                            borderRadius: "50%",
                            border: compact
                                ? "25px solid rgba(255, 255, 255, 0.075)"
                                : "42px solid rgba(255, 255, 255, 0.075)",
                            transition:
                                "width 650ms cubic-bezier(0.22, 1, 0.36, 1), height 650ms cubic-bezier(0.22, 1, 0.36, 1), inset 650ms cubic-bezier(0.22, 1, 0.36, 1), border-width 650ms cubic-bezier(0.22, 1, 0.36, 1)",
                        },
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: compact ? 120 : 205,
                            height: compact ? 120 : 205,
                            left: compact ? -86 : -122,
                            bottom: compact ? -88 : -116,
                            borderRadius: "50%",
                            border: compact
                                ? "21px solid rgba(255, 255, 255, 0.055)"
                                : "36px solid rgba(255, 255, 255, 0.055)",
                            transition:
                                "width 650ms cubic-bezier(0.22, 1, 0.36, 1), height 650ms cubic-bezier(0.22, 1, 0.36, 1), inset 650ms cubic-bezier(0.22, 1, 0.36, 1), border-width 650ms cubic-bezier(0.22, 1, 0.36, 1)",
                        },
                        "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                            "&::before, &::after": {
                                transition: "none",
                            },
                        },
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: compact
                                ? { xs: "center", sm: "flex-start" }
                                : "flex-start",
                            gap: compact ? { xs: 0, sm: 1.25 } : 1.5,
                            transition:
                                "gap 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                            "@media (prefers-reduced-motion: reduce)": {
                                transition: "none",
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src="/images/logo512_large.png"
                            alt=""
                            aria-hidden="true"
                            sx={{
                                width: compact ? { xs: 46, sm: 50 } : 58,
                                height: compact ? { xs: 46, sm: 50 } : 58,
                                flexShrink: 0,
                                borderRadius: compact ? 1.5 : 1.75,
                                border: "1px solid rgba(255, 255, 255, 0.27)",
                                boxShadow: "0 9px 25px rgba(0, 64, 27, 0.2)",
                                transition:
                                    "width 500ms cubic-bezier(0.22, 1, 0.36, 1), height 500ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 500ms ease",
                                "@media (prefers-reduced-motion: reduce)": {
                                    transition: "none",
                                },
                            }}
                        />
                        <Box
                            sx={{
                                maxWidth: compact ? { xs: 0, sm: 180 } : 180,
                                opacity: compact ? { xs: 0, sm: 1 } : 1,
                                minWidth: 0,
                                overflow: "hidden",
                                transform: compact
                                    ? {
                                          xs: "translateX(-6px)",
                                          sm: "translateX(0)",
                                      }
                                    : "translateX(0)",
                                transition:
                                    "max-width 500ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease, transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                                "@media (prefers-reduced-motion: reduce)": {
                                    transition: "none",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "DraigRegular",
                                    fontSize: compact ? 21 : 23,
                                    lineHeight: 1,
                                    letterSpacing: "0.02em",
                                    transition: "font-size 400ms ease",
                                    "@media (prefers-reduced-motion: reduce)": {
                                        transition: "none",
                                    },
                                }}
                            >
                                Nodiant
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    mt: 0.2,
                                    color: "rgba(255, 255, 255, 0.68)",
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                }}
                            >
                                Online
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        aria-hidden={compact}
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            display: { xs: "none", sm: "block" },
                            maxHeight: compact ? 0 : 120,
                            opacity: compact ? 0 : 1,
                            overflow: "hidden",
                            transform: compact
                                ? "translateY(14px)"
                                : "translateY(0)",
                            transition: compact
                                ? "max-height 360ms cubic-bezier(0.22, 1, 0.36, 1), opacity 160ms ease, transform 260ms ease"
                                : "max-height 560ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease 220ms, transform 380ms cubic-bezier(0.22, 1, 0.36, 1) 190ms",
                            "@media (prefers-reduced-motion: reduce)": {
                                transition: "none",
                            },
                        }}
                    >
                        <Typography
                            component="p"
                            sx={{
                                fontFamily: "DraigRegular",
                                fontSize: 31,
                                lineHeight: 1.08,
                                letterSpacing: "0.01em",
                            }}
                        >
                            Welcome to Nodiant
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                maxWidth: 270,
                                mt: 1,
                                color: "rgba(255, 255, 255, 0.72)",
                                lineHeight: 1.55,
                            }}
                        >
                            Everything shared with you will be waiting here.
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 0,
                        px: compact
                            ? { xs: 1.75, sm: 2.5 }
                            : { xs: 2.5, sm: 4.5 },
                        py: compact ? 1.5 : { xs: 3, sm: 4 },
                        backgroundColor: "background.paper",
                        transition:
                            "gap 550ms cubic-bezier(0.22, 1, 0.36, 1), padding 600ms cubic-bezier(0.22, 1, 0.36, 1)",
                        "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                        },
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                display: "block",
                                mb: compact ? 0.25 : 0.6,
                                color: "primary.main",
                                fontWeight: 800,
                                letterSpacing: "0.035em",
                                transition: "margin 400ms ease",
                                "@media (prefers-reduced-motion: reduce)": {
                                    transition: "none",
                                },
                            }}
                        >
                            {greeting}
                        </Typography>
                        <Box
                            sx={{
                                position: "relative",
                                minHeight: compact
                                    ? { xs: 24, sm: 25 }
                                    : { xs: 30, sm: 36 },
                                transition:
                                    "min-height 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                                "@media (prefers-reduced-motion: reduce)": {
                                    transition: "none",
                                },
                            }}
                        >
                            <Typography
                                component="h2"
                                aria-hidden={!compact}
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    fontFamily: "DraigRegular",
                                    fontSize: {
                                        xs: "clamp(1.05rem, 5vw, 1.3rem)",
                                        sm: 22,
                                    },
                                    lineHeight: 1.12,
                                    letterSpacing: "0.01em",
                                    whiteSpace: {
                                        xs: "nowrap",
                                        sm: "normal",
                                    },
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    opacity: compact ? 1 : 0,
                                    transform: compact
                                        ? "translateY(0)"
                                        : "translateY(-6px)",
                                    transition: compact
                                        ? "opacity 240ms ease 150ms, transform 320ms ease 110ms"
                                        : "opacity 130ms ease, transform 220ms ease",
                                    "@media (prefers-reduced-motion: reduce)": {
                                        transition: "none",
                                    },
                                }}
                            >
                                Welcome to Nodiant
                            </Typography>
                            <Typography
                                component="h2"
                                aria-hidden={compact}
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    fontFamily: "DraigRegular",
                                    fontSize: { xs: 25, sm: 32 },
                                    lineHeight: 1.12,
                                    letterSpacing: "0.01em",
                                    opacity: compact ? 0 : 1,
                                    transform: compact
                                        ? "translateY(8px)"
                                        : "translateY(0)",
                                    transition: compact
                                        ? "opacity 120ms ease, transform 220ms ease"
                                        : "opacity 280ms ease 170ms, transform 360ms cubic-bezier(0.22, 1, 0.36, 1) 140ms",
                                    "@media (prefers-reduced-motion: reduce)": {
                                        transition: "none",
                                    },
                                }}
                            >
                                Your feed starts here
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            component="p"
                            aria-hidden={compact}
                            sx={{
                                mt: compact ? 0 : 1,
                                maxWidth: 510,
                                maxHeight: compact ? 0 : 80,
                                opacity: compact ? 0 : 1,
                                color: "text.secondary",
                                lineHeight: 1.6,
                                overflow: "hidden",
                                transform: compact
                                    ? "translateY(8px)"
                                    : "translateY(0)",
                                transition: compact
                                    ? "max-height 300ms cubic-bezier(0.22, 1, 0.36, 1), margin 300ms ease, opacity 140ms ease, transform 220ms ease"
                                    : "max-height 520ms cubic-bezier(0.22, 1, 0.36, 1), margin 420ms ease, opacity 280ms ease 210ms, transform 360ms cubic-bezier(0.22, 1, 0.36, 1) 180ms",
                                "@media (prefers-reduced-motion: reduce)": {
                                    transition: "none",
                                },
                            }}
                        >
                            When files, surveys or other updates are shared with
                            you, they’ll appear in this feed and stay here for
                            you to revisit.
                        </Typography>

                        <Box
                            aria-hidden={compact}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                                mt: compact ? 0 : 2,
                                maxHeight: compact ? 0 : 24,
                                opacity: compact ? 0 : 1,
                                overflow: "hidden",
                                color: "text.secondary",
                                transform: compact
                                    ? "translateY(8px)"
                                    : "translateY(0)",
                                transition: compact
                                    ? "max-height 260ms ease, margin 260ms ease, opacity 120ms ease, transform 200ms ease"
                                    : "max-height 420ms ease, margin 420ms ease, opacity 260ms ease 300ms, transform 360ms cubic-bezier(0.22, 1, 0.36, 1) 260ms",
                                "@media (prefers-reduced-motion: reduce)": {
                                    transition: "none",
                                },
                            }}
                        >
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />
                            <Typography variant="caption">
                                {today.format("dddd, D MMMM YYYY")}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        aria-hidden={!compact}
                        sx={{
                            ml: "auto",
                            display: { xs: "none", sm: "flex" },
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 1,
                            maxWidth: compact ? 180 : 0,
                            opacity: compact ? 1 : 0,
                            overflow: "hidden",
                            flexShrink: 0,
                            color: "text.secondary",
                            transform: compact
                                ? "translateX(0)"
                                : "translateX(10px)",
                            transition: compact
                                ? "max-width 480ms cubic-bezier(0.22, 1, 0.36, 1) 120ms, opacity 240ms ease 180ms, transform 360ms ease 120ms"
                                : "max-width 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 140ms ease, transform 260ms ease",
                            "@media (prefers-reduced-motion: reduce)": {
                                transition: "none",
                            },
                        }}
                    >
                        <CalendarTodayOutlinedIcon
                            sx={{ fontSize: 19, flexShrink: 0 }}
                        />
                        <Box
                            sx={{
                                minWidth: 0,
                                textAlign: "left",
                                whiteSpace: "nowrap",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                    color: "text.primary",
                                }}
                            >
                                {today.format("dddd")}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    lineHeight: 1.35,
                                }}
                            >
                                {today.format("D MMMM YYYY")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Card>
    );
}
